import {useCallback, useEffect, useMemo} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {Outlet, useLocation} from 'react-router-dom';
import {Analytics, ANALYTIC_EVENTS, useIVStore, useStepperStore} from '@chekin/shared';
import {preloadDocumentGuide, preloadSelfieGuide} from '@chekin/shared/guest-form';
import {IdentityVerificationSkeleton} from './components/IdentityVerificationSkeleton';
import {VerificationCompletedProvider} from 'components/VerificationCompletedModal';
import {useRegisterNavigationGuard} from 'context/navigationGuard';
import {PATHS} from 'routes/constants';
import {useIVOnProperty} from 'hooks/useIVOnProperty';
import {useIVComputedDetails} from 'hooks';
import {IVUnavailableView} from './views/IVUnavailableView';
import {useIVRemoteSync} from 'hooks/iv/useIVRemoteSync';
import {useIVRemoteEvents} from 'hooks/iv/useIVRemoteHandoff';
import {getScreenInfo} from 'analytics/posthog/getScreenInfo';

const IV_GUARD_EXCLUDED_VIEWS = [PATHS.verification.main, PATHS.verification.qrScan];

function useIVNavigationGuard() {
  const {t} = useTranslation();
  const {pathname} = useLocation();
  const {resetAllIVState} = useIVStore();
  const {resetIVProcess} = useStepperStore();

  const lastSegment = pathname.split('/').filter(Boolean).pop() ?? '';
  const isMidIV = !IV_GUARD_EXCLUDED_VIEWS.includes(lastSegment);

  const discardIV = useCallback(() => {
    Analytics.track(ANALYTIC_EVENTS.ivLeaving, {step: getScreenInfo(pathname).step});
    resetIVProcess();
    resetAllIVState();
  }, [resetIVProcess, resetAllIVState, pathname]);

  const description = useMemo(
    () => (
      <Trans i18nKey="discard_iv_changes_text">
        Leaving the process before completing the registration will
        <b>discard the photos</b> uploaded <b>or the data</b> completed so far and you
        will have to start over.
      </Trans>
    ),
    [],
  );

  useRegisterNavigationGuard({
    when: isMidIV,
    onDiscard: discardIV,
    title: t('discard_changes'),
    description,
  });
}

function useIVGuidePreload() {
  const {biomatch} = useIVComputedDetails();

  useEffect(() => {
    preloadDocumentGuide();
  }, []);

  useEffect(() => {
    if (biomatch) preloadSelfieGuide();
  }, [biomatch]);
}

function IdentityVerificationLayout() {
  const {isLoaded} = useIVRemoteSync();
  const {isIVOnPropertyUnavailable} = useIVOnProperty();

  useIVNavigationGuard();
  useIVRemoteEvents();
  useIVGuidePreload();

  const renderContent = () => {
    if (!isLoaded) {
      return <IdentityVerificationSkeleton />;
    }

    if (isIVOnPropertyUnavailable) {
      return <IVUnavailableView />;
    }

    return <Outlet />;
  };

  return <VerificationCompletedProvider>{renderContent()}</VerificationCompletedProvider>;
}

export {IdentityVerificationLayout};
